/**
 * Cargar algo de la red sin dejar la pantalla colgada.
 *
 * Seis pantallas hacían `void listarLoQueSea().then(setEstado)` **sin `.catch`**. Con la red
 * caída, o con el servidor devolviendo un 500, la promesa se rechazaba en silencio, el estado se
 * quedaba en `null` y la pantalla decía «Cargando…» para siempre. Es el peor fallo posible en una
 * interfaz: quien lo sufre no sabe si esperar, si recargar o si el problema es suyo.
 *
 * Aquí hay tres estados de verdad —cargando, fallo, listo— y el fallo siempre ofrece reintentar
 * sin recargar la página.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Aviso } from './aviso.js';
import { Boton } from './boton.js';

/**
 * Cuánto se espera antes de dar la carga por perdida.
 *
 * Es un cinturón, no el arreglo: el arreglo es el `catch` de abajo, sin el cual el estado se
 * quedaba en «cargando» de verdad para siempre. Cortando la red con la pantalla abierta, la
 * petición tarda **7 segundos** en rendirse y rechazar, así que este plazo casi nunca llega a
 * saltar. Está para el caso que el `catch` no cubre: un servidor que acepta la conexión y no
 * contesta nunca, que es lo que hace un proxy mal configurado.
 *
 * Doce segundos: más que la wifi lenta de un centro, menos de lo que se tarda en pensar que la
 * aplicación se ha muerto.
 */
const PLAZO_MS = 12_000;

export type EstadoRecurso<T> =
  | { readonly estado: 'cargando' }
  | { readonly estado: 'fallo' }
  | { readonly estado: 'listo'; readonly datos: T };

export interface Recurso<T> {
  readonly valor: EstadoRecurso<T>;
  /** Vuelve a pedirlo. Se usa en el botón de reintentar y tras cada cambio. */
  readonly recargar: () => void;
  /** Cambia los datos ya cargados sin pasar por la red, para reflejar algo al instante. */
  readonly ajustar: (cambio: (datos: T) => T) => void;
}

export function useRecurso<T>(
  pedir: () => Promise<T>,
  dependencias: readonly unknown[],
): Recurso<T> {
  const [valor, setValor] = useState<EstadoRecurso<T>>({ estado: 'cargando' });
  const [intento, setIntento] = useState(0);

  // `pedir` se recrea en cada render de quien llama, así que las dependencias las decide quien
  // usa el hook. Lo contrario —depender de la función— pediría los datos en bucle.
  const pedirRef = useCallback(pedir, dependencias);

  useEffect(() => {
    let vigente = true;

    const plazo = setTimeout(() => {
      if (vigente) {
        console.error('La carga ha superado el plazo');
        setValor({ estado: 'fallo' });
        vigente = false;
      }
    }, PLAZO_MS);

    setValor({ estado: 'cargando' });

    pedirRef()
      .then((datos) => {
        // Si la pantalla ya cambió, la respuesta que llega tarde no debe pisar nada.
        if (vigente) setValor({ estado: 'listo', datos });
      })
      .catch((error: unknown) => {
        console.error('No se ha podido cargar', error);
        if (vigente) setValor({ estado: 'fallo' });
      })
      .finally(() => clearTimeout(plazo));

    return () => {
      vigente = false;
      clearTimeout(plazo);
    };
  }, [pedirRef, intento]);

  return {
    valor,
    recargar: () => setIntento((n) => n + 1),
    ajustar: (cambio) =>
      setValor((actual) =>
        actual.estado === 'listo' ? { estado: 'listo', datos: cambio(actual.datos) } : actual,
      ),
  };
}

/**
 * Lo que se ve mientras carga, cuando falla y cuando no hay nada.
 *
 * El vacío no es un error: es la primera vez de alguien, y merece decir qué es esto y qué hacer,
 * no una línea gris.
 */
export function VistaRecurso<T>({
  recurso,
  vacio,
  children,
}: {
  readonly recurso: Recurso<T>;
  /** Qué enseñar cuando la carga fue bien pero no hay nada. */
  readonly vacio?: {
    readonly titulo: string;
    readonly pie: string;
    readonly accion?: React.ReactNode;
  };
  readonly children: (datos: T) => React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (recurso.valor.estado === 'cargando') {
    return (
      <p role="status" className="text-micro font-display text-text-dim mt-6 font-bold uppercase">
        {t('common.loading')}
      </p>
    );
  }

  if (recurso.valor.estado === 'fallo') {
    return (
      <Aviso tono="error" interrumpe className="mt-6">
        <p className="font-display font-bold">{t('common.loadFailedTitle')}</p>
        <p className="text-body text-text-dim mt-1">{t('common.loadFailedLead')}</p>
        <Boton variante="secundaria" className="mt-3" onClick={recurso.recargar}>
          {t('common.retry')}
        </Boton>
      </Aviso>
    );
  }

  const datos = recurso.valor.datos;
  const estaVacio = Array.isArray(datos) && datos.length === 0;

  if (estaVacio && vacio !== undefined) {
    return (
      <div className="rounded-card border-line mt-6 flex flex-col items-center gap-3 border-2 border-dashed p-8 text-center">
        <p className="font-display text-h3 font-bold">{vacio.titulo}</p>
        <p className="text-body text-text-dim">{vacio.pie}</p>
        {vacio.accion}
      </div>
    );
  }

  return <>{children(datos)}</>;
}
